import { CheckCircle2, Download } from 'lucide-react';
import { AmendmentModel, PreflightCopy, ReportStatus } from '@signalvote/shared';
import type { ComplianceReportDetailDto, FilingTransport } from '@signalvote/shared';
import { PendingButton } from '@/components/ui/pending-button';
import { US_STATE_OPTIONS } from '@/lib/usStates';
import { ReceiptEditor } from './ReceiptEditor';
import { pendingAtFilingCount } from './reportUtils';

interface PostFileSuccessPanelProps {
  report: ComplianceReportDetailDto;
  /**
   * Reuses `ReportDetailPage`'s existing package-download handler (FIN-010
   * `GET /reports/{id}/package`) — no new endpoint or mutation. The panel only
   * surfaces a second affordance for the moment right after filing.
   */
  onReDownload: () => void;
  downloading?: boolean;
  /**
   * ADR-054 — download the signable CRO-1000 cover distinctly from the package
   * zip (NC filing is a two-artifact requirement). Only wired for NC; the button
   * renders when this handler is present.
   */
  onDownloadCover?: () => void;
  downloadingCover?: boolean;
  /**
   * #481 — the served filing-TRANSPORT axis (`profile.filingTransport`, #478), resolved at the mount
   * (`ReportDetailPage`, via `usePortalUploadModel`). Generalises the prior 2-value `isPortalUpload`
   * gate to the full axis so the panel reads transport from ONE served signal instead of re-deriving
   * it from acronyms:
   *   • `portal` (AL/VA/LA) — the treasurer uploads the generated file to a state portal, which is
   *     the filing step; shows the upload step + (VA/LA) confirmation-number capture.
   *   • `email` (NC, MS) — no portal; branches to the self-send steps (destination from
   *     the transport-neutral `filingDeliveryInstructions`), no confirmation capture.
   *   • `inSystem` (federal) — generate = file; NO portal step, NO portal-issued confirmation number.
   *   • `agentEntry` (NJ) — a portal-entry run enters the report, but the manual upload path stays,
   *     so it gets the same steps as `portal`.
   *   • `null` while the profile loads or for a rules-only ruleset.
   * The panel derives the portal-upload sub-gate from `portal` or `agentEntry`, so a federal
   * filer is never shown a step that does not exist in the app's own federal model.
   */
  filingTransport: FilingTransport | null;
  /**
   * #571 — the served amendment-model axis (`profile.amendmentModel`, ADR-021), forwarded from
   * `ReportDetailPage`. The confirmation-number carve-out (AL's append-only FCPA portal issues none)
   * reads this one served axis instead of a `stateCode === 'AL'` boolean. `null` for a rules-only
   * ruleset or before the profile loads.
   */
  amendmentModel: AmendmentModel | null;
}

/**
 * FLW-004 — the post-file "what now" moment, composed from existing pieces
 * (design doc § Key decisions #8). Renders when a report flips to Filed in
 * session (TanStack Query invalidation re-renders with the new status) and on
 * revisit while no receipt is recorded; once a receipt is saved the panel
 * hides on the next render.
 *
 * The portal next-steps read the served `amendmentModel` (ADR-021) + `filingTransport`
 * axes forwarded from `ReportDetailPage`. The state portal's confirmation number IS the
 * receipt — `ReceiptEditor` (FIN-011) is the capture affordance.
 *
 * #296/#860 — an email-transport jurisdiction files by email (no portal, no confirmation number).
 * When the report carries `filingDeliveryInstructions` (the transport-neutral block) the panel
 * branches to the self-send copy — email the file to the resolved destination + (NC) deliver the
 * signed CRO-1000 cover to the NCSBE main office — and drops the receipt capture. The Portal states
 * use the shared path, with receipt capture limited to states that actually issue a confirmation
 * number (Alabama does not).
 */
export function PostFileSuccessPanel({
  report,
  onReDownload,
  downloading,
  onDownloadCover,
  downloadingCover,
  filingTransport,
  amendmentModel,
}: PostFileSuccessPanelProps) {
  // #481 — the portal-upload sub-gate is the `portal` arm of the served transport axis. `email`
  // (NC) and `inSystem` (federal) are excluded structurally here, so neither the upload step nor
  // the confirmation-number capture can render for them regardless of any per-state signal.
  const isPortalUpload = filingTransport === 'portal' || filingTransport === 'agentEntry';

  // Visibility (AC): Filed + no receipt recorded yet.
  if (report.status !== ReportStatus.Filed) return null;
  if (report.stateFilingReceiptId) return null;
  // Mirror the report-detail gate: while in-period transactions are still Pending
  // at filing, the celebratory "that's everything" panel is false — the report
  // page shows an actionable warning in its place. Both read pendingAtFilingCount
  // off the same `inclusion` flag so the two can never diverge (ADR-020).
  if (pendingAtFilingCount(report) > 0) return null;

  const stateName = stateNameFor(report.stateCode);
  const portalStep = PreflightCopy.SuccessPanelPortalStep.replace('{state}', stateName);

  // #860 — an EMAIL-transport filing carries transport-neutral delivery instructions (destination
  // address + optional secondary-delivery note). When present we branch to the self-send copy: an
  // email step (+ the jurisdiction's delivery note, e.g. NC's signed-cover step) instead of the
  // portal-upload step, and NO receipt capture ("record your confirmation number" is a dead end for
  // an email filer). The block is transport-derived, not a state-named field; the AL/VA/LA portal
  // path is a separate branch below (SuccessPanelPortalStep is untouched). NC renders identically.
  const delivery = report.filingDeliveryInstructions;
  // The confirmation-number capture is a PORTAL-UPLOAD concept: the state portal issues the number
  // when the treasurer uploads. It applies only when (a) the served transport is `portal`
  // (`isPortalUpload`) — this already excludes NC email and federal in-system, which issue none; and
  // (b) the jurisdiction is not append-only PortalOnly (AL), whose FCPA portal issues no confirmation
  // number. #571 — the AL carve-out is now read off the served `amendmentModel` (AL is the append-only
  // PortalOnly jurisdiction) instead of a `stateCode === 'AL'` boolean, so no state-code branch remains
  // in this amendment/receipt UI (the Finance golden rule forbids the state boolean).
  const recordsConfirmationNumber =
    isPortalUpload && amendmentModel !== AmendmentModel.PortalOnly;

  return (
    <div
      className="rounded-lg border border-green-300 bg-green-50 p-4 space-y-3"
      data-testid="post-file-success-panel"
    >
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-green-900">
            {PreflightCopy.SuccessPanelBuiltAndDownloaded}
          </p>
          {delivery ? (
            <>
              <p className="text-sm text-green-900" data-testid="nc-cfd-step">
                {PreflightCopy.SuccessPanelNcCfdStep.replace('{inbox}', delivery.destinationAddress)}
              </p>
              <p className="text-sm text-green-900" data-testid="nc-cover-step">
                {PreflightCopy.SuccessPanelNcCoverStep}
              </p>
              {/* #860 — an optional server-supplied secondary-delivery note (transport-neutral). Null
                  for NC and today's email filers; the forward slot for a jurisdiction whose delivery
                  note is dynamic (e.g. a Mississippi county filer's Circuit Clerk address). */}
              {delivery.deliveryNote && (
                <p className="text-sm text-green-900" data-testid="delivery-note">
                  {delivery.deliveryNote}
                </p>
              )}
              {report.isAmendment && (
                <p className="text-sm text-green-900" data-testid="nc-amendment-line">
                  {PreflightCopy.SuccessPanelNcAmendmentLine}
                </p>
              )}
            </>
          ) : (
            <>
              {/* Portal-upload jurisdictions (AL/VA/LA) get the "upload at the {state} portal"
                  step. Federal is in-system e-file (generate = file) — no portal step exists, so
                  it shows only the built-and-downloaded + closing copy, which IS its success
                  message. Gating on isPortalUpload stops the nonsense "federal filing portal" line. */}
              {isPortalUpload && <p className="text-sm text-green-900">{portalStep}</p>}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <PendingButton
          variant="outline"
          size="sm"
          pending={downloading}
          icon={<Download className="h-4 w-4" />}
          onClick={onReDownload}
          data-testid="success-redownload-button"
        >
          {downloading ? 'Downloading...' : 'Download package'}
        </PendingButton>

        {/* ADR-054 — NC files two artifacts; the treasurer needs the signable cover distinctly. The
            cover-download handler is only wired for NC, so it gates the button even with the block. */}
        {delivery && onDownloadCover && (
          <PendingButton
            variant="outline"
            size="sm"
            pending={downloadingCover}
            icon={<Download className="h-4 w-4" />}
            onClick={onDownloadCover}
            data-testid="success-download-cover-button"
          >
            {downloadingCover ? 'Downloading...' : 'Download signed cover (CRO-1000)'}
          </PendingButton>
        )}
      </div>

      {/* Alabama and NC issue no confirmation number; other portal states retain capture. */}
      {recordsConfirmationNumber && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-green-800 mb-1">
            Record your confirmation number
          </p>
          <ReceiptEditor
            reportId={report.id}
            receiptId={report.stateFilingReceiptId}
            receiptAt={report.stateFilingReceiptAt}
            concurrencyVersion={report.concurrencyVersion}
          />
        </div>
      )}

      <p className="text-sm text-green-900">{PreflightCopy.SuccessPanelClosingCopy}</p>
    </div>
  );
}

/**
 * Resolves a state code to its display name for the `{state}` token. Falls back
 * to the raw code, then to "state", so the portal line always reads sensibly.
 */
function stateNameFor(stateCode: string | null): string {
  if (!stateCode) return 'state';
  const match = US_STATE_OPTIONS.find((s) => s.value === stateCode.toUpperCase());
  return match ? match.label : stateCode;
}
